import BookIcon from "@ecologi/assets/icons/book.svg";
import LocationIcon from "@ecologi/assets/icons/location_alt.svg";
import {
  campaigns,
  CampaignType,
  getProjectMapUrl,
  Impact,
  isSubProfileType,
  isTree,
  pluralise,
  SubProfile,
  Tile,
  User,
  Wordpress,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import React from "react";
import { ForestModalBase } from "./ForestModalBase";
import { ModalInfoCard } from "./ModalInfoCard";
import { ReferralUserLink } from "./ReferralUserLink";
import {
  getAgeInDays,
  getFundedBy,
  getFundedDaysAgo,
  getLocationAndProjectLabel,
  getModalImagePath,
  getTitle,
  getTreeNumbering,
  getTreeSpecies,
} from "./utils";
import { COUNTRY_ICONS } from "./consts";
import {
  ProjectContent,
  Title,
  TitleWrapper,
  Text,
  Grid,
  GridIcon,
  StyledLink,
  Bold,
} from "./styles/ForestModal.styles";

const CountryFlag = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  margin-left: 8px;
`;

const ContentWrapper = styled.div`
  margin-bottom: 10px;
`;

const LinkWrapper = styled.div`
  width: fit-content;
`;

const SubProfileWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.mist};
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 30px 40px 40px 30px;
`;

const SubProfileTitle = styled.div`
  font-weight: bold;
`;

export const ForestTreeModal = ({
  campaign,
  closePopup,
  isOpen,
  isOwnProfile,
  project,
  tileInfo: { tile, impact, isMocked },
  user,
}: {
  campaign?: CampaignType | null;
  closePopup: () => void;
  isOpen: boolean;
  isOwnProfile: boolean;
  project?: Wordpress.WordpressProjectType;
  tileInfo: { tile: Tile; impact: Impact; isMocked: boolean };
  user: User | null;
}) => {
  if (!isTree(impact)) return null;

  const subProfile = isSubProfileType(tile) && new SubProfile(tile);

  const title = subProfile
    ? `${isSubProfileType(tile) && tile.username} sub-profile`
    : getTitle(impact);

  const fundedBy = getFundedBy(user, impact);
  const fundedDaysAgo = getFundedDaysAgo(getAgeInDays(impact));
  const hasFundedInfo =
    fundedDaysAgo && !impact.referred && campaign !== campaigns.forestOfLove.id; // Bespoke profile design

  const [location, projectLabel] =
    (project && getLocationAndProjectLabel(impact, project)) || [];

  const projectHref = `/projects/${project?.slug}`;
  const projectMapUrl = project && getProjectMapUrl(project.location);

  const imageLabel =
    impact.variant === "pending"
      ? "Coming soon!"
      : impact.what3Words
      ? "Your actual tree!"
      : undefined;

  const isReferral =
    impact.referred || (impact.referral && impact.referralUser);
  const numbering = getTreeNumbering(impact);
  const numberingLabel = `${pluralise(impact.value, "Tree number", false)}: `;
  const species = getTreeSpecies(impact, project);

  return (
    <ForestModalBase
      closePopup={closePopup}
      enableSharing={!isMocked}
      imageLabel={imageLabel}
      imagePath={getModalImagePath(impact, project, !!subProfile)}
      isOpen={isOpen}
      isSubProfile={!!subProfile}
    >
      <>
        <ProjectContent>
          <TitleWrapper>
            <Title data-testid="forest-modal-project-title">{title}</Title>

            {impact.localProject && (
              <CountryFlag>
                {COUNTRY_ICONS[impact.localProject.country].flag}
              </CountryFlag>
            )}
          </TitleWrapper>

          {impact.variant === "pending" ? (
            <Text>
              This tree is on the way to its special planting site. Check back
              soon to see the photo of your tree 🍃
            </Text>
          ) : !!subProfile ? (
            <SubProfileWrapper>
              <SubProfileTitle>What's this?</SubProfileTitle>
              <ContentWrapper>
                {subProfile.name} is like a little village in the bigger town of{" "}
                {user?.username}. They're part of the group, but are also doing
                their own thing to protect the planet.
              </ContentWrapper>
              <LinkWrapper>
                <StyledLink
                  href={`/${user?.username}/${subProfile.name}`}
                  target="_blank"
                  title="View sub-profile"
                  rel="noopener"
                >
                  Check out their impact here.
                </StyledLink>
              </LinkWrapper>
            </SubProfileWrapper>
          ) : (
            <>
              {hasFundedInfo && (
                <Text data-testid="forest-modal-funded-by">
                  <Bold>{fundedBy ? "Funded by: " : "Funded: "}</Bold>
                  {fundedBy ? `${fundedBy}, ${fundedDaysAgo}` : fundedDaysAgo}
                </Text>
              )}

              {species && (
                <Text>
                  <Bold>Species: </Bold>
                  {species}
                </Text>
              )}

              {numbering && (
                <Text>
                  <Bold>{numberingLabel}</Bold>
                  {numbering}
                </Text>
              )}

              {isReferral && (
                <Text>
                  <Bold>
                    {impact.referred ? "Referred by: " : "Referred: "}
                  </Bold>
                  {impact.referralUser ? (
                    <ReferralUserLink referralUser={impact.referralUser} />
                  ) : (
                    impact.name
                  )}
                </Text>
              )}

              <Grid>
                {(impact.what3Words || projectMapUrl) && (
                  <>
                    <GridIcon>
                      <LocationIcon />
                    </GridIcon>

                    <Text>
                      {<Bold>Location: </Bold>}
                      <StyledLink
                        href={
                          projectMapUrl ||
                          `https://what3words.com/${impact.what3Words}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        title={
                          projectMapUrl
                            ? `View the project in ${location} (new tab)`
                            : "View your tree on the What3Words map (new tab)"
                        }
                      >
                        {location || "Map"}
                      </StyledLink>
                    </Text>
                  </>
                )}

                <GridIcon>
                  <BookIcon />
                </GridIcon>

                <Text>
                  <Bold>Project: </Bold>
                  <StyledLink
                    href={projectHref}
                    rel="noopener noreferrer"
                    target="_blank"
                    title="View project"
                  >
                    {projectLabel}
                  </StyledLink>
                </Text>
              </Grid>
            </>
          )}
        </ProjectContent>

        <ModalInfoCard impact={impact} isOwnProfile={isOwnProfile} />
      </>
    </ForestModalBase>
  );
};
